'use client';

import Icon from '@ant-design/icons';
import { Modal, Spin, message } from 'antd';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import RightSvg from '@/components/Icon/svgs/right.svg';
import LottieJumpLoading from '@/components/chat-app/components/LottieJumpLoading';
import { useTranslation } from '@/i18n/client';
import {
  EApplyHistoryStatus,
  EApplyStatus,
  EReviewOperationType,
  type ICircleApplyItem,
  circleApplyHistory,
  circleApplyReview
} from '@/service/friend';
import { defaultHomeLogo } from '@/utils/operation';

import styles from './MemberRequestsPage.less';

// 阻止事件冒泡到父组件
const stopPropagationModalRender = (node: ReactNode) => (
  <div className="w-full h-full" onClick={(e) => e.stopPropagation()}>
    {node}
  </div>
);

interface IProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  circleRoute: string;
  handleUpdate?: () => void;
}

export const MemberRequestsPage = (props: IProps): JSX.Element => {
  const { open, setOpen, circleRoute, handleUpdate } = props;
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [pendingList, setPendingList] = useState<ICircleApplyItem[]>([]);
  const [processedList, setProcessedList] = useState<ICircleApplyItem[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // 关闭弹窗
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  // 获取申请列表
  const fetchApplyList = useCallback(async () => {
    if (!circleRoute) return;

    setLoading(true);

    try {
      // 获取待处理申请
      const pendingRes = await circleApplyHistory({
        route: circleRoute,
        status: EApplyHistoryStatus.PENDING
      });

      if (pendingRes.data.code === 0) {
        setPendingList(pendingRes.data.data.list);
      }

      // 获取已处理申请
      const processedRes = await circleApplyHistory({
        route: circleRoute,
        status: EApplyHistoryStatus.PROCESSED
      });

      if (processedRes.data.code === 0) {
        setProcessedList(
          processedRes.data.data.list.filter((item) => item.status !== EApplyStatus.NO_VERIFY)
        );
      }
    } catch (err) {
      console.error('Failed to fetch apply list:', err);
    } finally {
      setLoading(false);
    }
  }, [circleRoute]);

  useEffect(() => {
    if (open) {
      fetchApplyList();
    }
  }, [open, fetchApplyList]);

  // 处理申请
  const handleReview = async (applyId: string, operationType: EReviewOperationType) => {
    setActionLoading(applyId);

    try {
      const res = await circleApplyReview({
        operationType,
        applyIds: [applyId]
      });

      if (res.data.code !== 0) {
        throw new Error(res.data.message || t('error.oops'));
      }

      message.success(
        operationType === EReviewOperationType.APPROVE
          ? t('circle.memberRequests.approveSuccess')
          : t('circle.memberRequests.declineSuccess')
      );
      fetchApplyList();
      handleUpdate?.();
    } catch (e: unknown) {
      const error = e as Error;

      message.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  // 全部同意
  const handleApproveAll = async () => {
    if (pendingList.length === 0) return;

    setActionLoading('all');

    try {
      const res = await circleApplyReview({
        operationType: EReviewOperationType.APPROVE,
        applyIds: pendingList.map((item) => item.id)
      });

      if (res.data.code !== 0) {
        throw new Error(res.data.message || t('error.oops'));
      }

      message.success(t('circle.memberRequests.approveAllSuccess'));
      fetchApplyList();
      handleUpdate?.();
    } catch (e: unknown) {
      const error = e as Error;

      message.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  // 获取状态文本
  const getStatusText = (status: EApplyStatus) => {
    switch (status) {
      case EApplyStatus.APPROVED:
        return t('circle.memberRequests.approved');
      case EApplyStatus.REJECTED:
        return t('circle.memberRequests.declined');
      case EApplyStatus.PENDING:
        return t('circle.memberRequests.pending');
      default:
        return '';
    }
  };

  // 渲染待处理项
  const renderPendingItem = (item: ICircleApplyItem) => (
    <div key={item.id} className={styles.requestItem}>
      <div className={styles.requestAvatar}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img alt={item.applicant.username} src={item.applicant.avatar} />
      </div>
      <div className={styles.requestContent}>
        {/* 上方：用户名 + 操作按钮 */}
        <div className={styles.requestHeader}>
          <span className={styles.requestName}>{item.applicant.username}</span>
          <div className={styles.requestActions}>
            <button
              className={styles.declineBtn}
              disabled={actionLoading === item.id}
              onClick={() => handleReview(item.id, EReviewOperationType.REJECT)}
              type="button"
            >
              {t('circle.memberRequests.decline')}
            </button>
            <button
              className={styles.approveBtn}
              disabled={actionLoading === item.id}
              onClick={() => handleReview(item.id, EReviewOperationType.APPROVE)}
              type="button"
            >
              {actionLoading === item.id ? (
                <Spin size="small" />
              ) : (
                t('circle.memberRequests.approve')
              )}
            </button>
          </div>
        </div>
        {/* 下方：申请备注 */}
        {item.answer && (
          <div className={styles.requestNote}>
            <span className={styles.noteLabel}>{t('circle.memberRequests.requestNote')}:</span>
            <span className={styles.noteText}>{item.answer}</span>
          </div>
        )}
      </div>
    </div>
  );

  // 渲染已处理项
  const renderProcessedItem = (item: ICircleApplyItem) => (
    <div key={item.id} className={classNames(styles.requestItem, styles.processed)}>
      <div className={styles.requestAvatar}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img alt={item.applicant.username} src={item.applicant.avatar} />
      </div>
      <div className={styles.requestContent}>
        {/* 上方：用户名 + 状态 */}
        <div className={styles.requestHeader}>
          <span className={styles.requestName}>{item.applicant.username}</span>
          <span
            className={classNames(styles.requestStatus, {
              [styles.approved]: item.status === EApplyStatus.APPROVED,
              [styles.rejected]: item.status === EApplyStatus.REJECTED
            })}
          >
            {getStatusText(item.status)}
          </span>
        </div>
        {/* 下方：申请备注 */}
        {item.answer && (
          <div className={styles.requestNote}>
            <span className={styles.noteLabel}>{t('circle.memberRequests.requestNote')}:</span>
            <span className={styles.noteText}>{item.answer}</span>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <Modal
        centered
        className={styles.memberRequestsModal}
        closable={false}
        destroyOnClose
        footer={null}
        maskClosable
        modalRender={stopPropagationModalRender}
        onCancel={handleClose}
        open={open}
        width={1000}
        zIndex={1100}
      >
        <div className={styles.memberRequestsPage}>
          {/* 头部 */}
          <div className={styles.headerContainer}>
            <div className={styles.backBtn} onClick={handleClose}>
              <Icon className={styles.backIcon} component={RightSvg} />
            </div>
            <span className={styles.title}>{t('circle.memberRequests.title')}</span>
            <div className={styles.placeholder} />
          </div>
          <div className={styles.loadingContainer}>
            <LottieJumpLoading className="!w-12 !h-12" />
          </div>
        </div>
      </Modal>
    );
  }

  const isEmpty = pendingList.length === 0 && processedList.length === 0;

  return (
    <Modal
      centered
      className={styles.memberRequestsModal}
      closable={false}
      destroyOnClose
      footer={null}
      maskClosable
      modalRender={stopPropagationModalRender}
      onCancel={handleClose}
      open={open}
      width={1000}
      zIndex={1100}
    >
      <div className={styles.memberRequestsPage}>
        {/* 头部 */}
        <div className={styles.headerContainer}>
          <div className={styles.backBtn} onClick={handleClose}>
            <Icon className={styles.backIcon} component={RightSvg} />
          </div>
          <span className={styles.title}>{t('circle.memberRequests.title')}</span>
          {pendingList.length > 0 ? (
            <button
              className={styles.approveAllBtn}
              disabled={actionLoading === 'all'}
              onClick={handleApproveAll}
              type="button"
            >
              {actionLoading === 'all' ? (
                <Spin size="small" />
              ) : (
                t('circle.memberRequests.approveAll')
              )}
            </button>
          ) : (
            <div className={styles.placeholder} />
          )}
        </div>

        {/* 内容区 */}
        <div className={styles.memberRequestsContent}>
          {isEmpty ? (
            <div className={styles.emptyContainer}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img alt="logo" className={styles.emptyLogo} src={defaultHomeLogo} />
              <div className={styles.emptyText}>
                <p>{t('circle.memberRequests.emptyLine1')}</p>
                <p>{t('circle.memberRequests.emptyLine2')}</p>
              </div>
            </div>
          ) : (
            <>
              {/* 待处理列表 */}
              {pendingList.length > 0 && (
                <div className={styles.requestSection}>
                  <div className={styles.sectionTitle}>{t('circle.memberRequests.pending')}</div>
                  <div className={styles.requestList}>{pendingList.map(renderPendingItem)}</div>
                </div>
              )}

              {/* 已处理列表 */}
              {processedList.length > 0 && (
                <div className={styles.requestSection}>
                  <div className={styles.sectionTitle}>{t('circle.memberRequests.processed')}</div>
                  <div className={styles.requestList}>{processedList.map(renderProcessedItem)}</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default MemberRequestsPage;
